'use client';

import type { ReactNode } from 'react';
import type { MediaView } from 'src/sections/search-all/constants/search-all-media';
import type { MatchSnippet } from 'src/sections/search-all/utils/search-all-match-snippet';
import type { DetailHighlight, MediaDetailOptions } from 'src/sections/search-all/utils/search-all-detail-options';

import { useMemo, useState, useCallback } from 'react';

import Stack from '@mui/material/Stack';

import { MediaGrid } from 'src/sections/search-all/components/media-grid';
import { MediaViewSwitch } from 'src/sections/search-all/components/media-view-switch';
import { MEDIA_DEFAULT_VIEW } from 'src/sections/search-all/constants/search-all-media';
import { useListMediaDetail } from 'src/sections/common/hooks/handler/use-list-media-detail';
import { sameList, orderCardsByKeys } from 'src/sections/search-all/utils/search-all-media-order';
import { type AccountProfile, applyAccountProfiles } from 'src/sections/search-all/utils/search-all-media-cards';
import { leadSnippet, cardMatchTexts, findMatchSnippet } from 'src/sections/search-all/utils/search-all-match-snippet';
import { useSearchAllAccountProfiles } from 'src/sections/search-all/hooks/load/fetch/use-search-all-account-profiles';

import { HubMediaDetail } from './hub-media-detail';
import { PUBLIC_NO_VIDEOS, toPublicStillCard } from '../public-share/public-still-card';

/**
 * 허브의 실물 격자 — 소재 찾기의 **격자와 상세 다이얼로그를 한 벌로** 쓴다.
 *
 * 🔑 부르는 쪽은 카드 목록만 넘긴다. 영상 주소·계정 얼굴·상세·비슷한 소재는 이 부품이 스스로 묻는다 —
 *   그래서 **소재 찾기와 보이는 것도 클릭 동작도 같다**(얼굴이 붙고, 카드를 누르면 상세가 열린다).
 * 🔑 머리 오른쪽에 **보기 고르개**를 둔다(섬네일·카드·리스트·테이블) — 어느 보기에서나 카드를 누르면 같은 상세가 열린다.
 *   상세는 **이 부품에 한 벌**이다(`HubMediaDetail`) — 이전/다음은 지금 보기가 편 순서를 따른다(테이블은 제 정렬 순서).
 *   ⚠️ 부르는 쪽이 **`view` 를 주면 고르개를 그리지 않는다** — 그 화면이 이미 제 고르개를 갖고 있다는 뜻이다
 *   (검색 화면이 그렇다 · 둘을 그리면 같은 줄에 고르개가 두 벌 선다).
 * ⛔ **광고·상품에는 보기 고르개를 두지 마라** — `view="thumb"` 로 못 박는다. 카드·리스트·테이블은
 *   «본문을 읽는 보기»라 본문이 없는 광고 소재·판매몰 상품에는 뜻이 없다. 고르개는 **소셜 글일 때만**이다.
 * 🔑 `selection` 을 받으면 **AI 에게 물을 소재를 고를 수 있다** — 화면이 장바구니를 쥐고 나눠 준다.
 * 🚨 읽기 전용(북마크·공유 감춤)은 `readOnly`·`publicView` 로만 정한다 — 담기(`selection`)가 없다고 읽기 전용이 되지 않는다.
 * ⚠️ 이어 불러오기는 기본으로 없다 — 부르는 쪽이 `more` 를 줄 때만 맨 아래에 닿으면 다음 쪽을 부른다(소재 찾기 격자의 `onReachEnd` 그대로).
 *   발치에 「마지막 소재입니다」는 두지 않는다.
 * ⚠️ 카드의 `videoAsk`·`video` 를 지워서 넘기지 마라 — 영상이 통째로 죽는다. 공유 페이지(`publicView`)만 이 부품이 스스로 지운다(아래).
 * 🔑 넓은 화면에서는 한 줄에 **세 장**이다(`perRow`) — 소재 찾기보다 카드가 커야 훑는 자리에서 읽힌다.
 * ⚠️ 발굴 목록에 없는 계정은 얼굴이 비어 머리글자로 떨어진다 — 앱에서 대신 만들지 않는다.
 */
export function HubMediaGrid({
  cards,
  perRow = HUB_PER_ROW,
  selection,
  view: controlledView,
  readOnly,
  overlay,
  match,
  socialOnly = false,
  detailOptions,
  loading = false,
  publicView,
  cardActions,
  singleColumnNarrow = false,
  highlight,
  emphasizedKeys,
  more,
}: {
  cards: Parameters<typeof MediaGrid>[0]['cards'];
  perRow?: number;
  /**
   * 카드 아래에 **왜 걸렸나**를 한 줄로 적을 말 — 허브는 브랜드·계정 이름이다.
   *
   * 🔑 소재 찾기가 검색어로 하는 것과 **같은 일**이다(`findMatchSnippet`) — 섬네일만 깔면
   *   「이 글이 왜 여기 있나」를 알 수 없어 카드를 하나씩 열어 봐야 한다.
   * ⚠️ 본문에 없고 이름에만 있으면 「작성자명」으로 알린다 · 둘 다 없으면 「본문 밖에서 찾음」이다.
   */
  match?: string;
  /** 부르는 쪽이 보기를 쥘 때 — 주면 이 부품은 고르개를 그리지 않는다 */
  view?: MediaView;
  /**
   * 소셜 글만 다루는 자리인가 — 켜면 상세의 「비슷한 소재」에서 **광고를 안 묻는다**.
   * 🚨 게시글 탭의 섬네일은 **보기만 바꾸는 기능**이다 — 같은 글을 촘촘히 깔 뿐이라
   *   다른 원장(광고·상품)이 끼면 「보기를 바꿨는데 내용이 달라졌다」가 된다.
   */
  socialOnly?: boolean;
  /** 상세가 더 싣는 것 — 글 목록 화면(게시물 탭)의 검색어 형광 · 「캠페인 추가」 · 수치 시점 */
  detailOptions?: MediaDetailOptions;
  /**
   * 화면이 찾은 말 — 카드·리스트·테이블 본문과 **상세 본문**에 칠한다.
   * `detailOptions.highlight` 를 따로 주면 상세는 그것을 따른다.
   */
  highlight?: DetailHighlight | null;
  /** 찾은 말이 든 카드 key — 테를 두른다(`MediaGrid` 의 같은 이름) */
  emphasizedKeys?: ReadonlySet<string>;
  /** 아직 받는 중인가 — 카드가 0장일 때만 소재 찾기와 같은 뼈대를 깐다 */
  loading?: boolean;
  /** 칸마다 얹는 화면의 동작 — 네 보기 모두에 선다(`MediaGrid` 의 `cardActions`) */
  cardActions?: Parameters<typeof MediaGrid>[0]['cardActions'];
  /** 모바일 밖에서도 격자가 좁으면 한 줄에 한 장(`MediaGrid` 의 같은 이름 · 모바일은 늘 한 장이다) */
  singleColumnNarrow?: boolean;
  /**
   * 읽기만 하는 자리인가 — 켜면 북마크·공유·담기를 감춘다. 기본은 `publicView` 를 받았는가다(공개 공유 화면).
   * ⚠️ 장바구니(`selection`)와 무관하다 — 담을 곳이 없어도 북마크·공유는 연다.
   */
  readOnly?: boolean;
  /** 카드마다 실물 위에 얹을 것 — 섬네일 보기에만 얹힌다 */
  overlay?: (card: Parameters<typeof MediaGrid>[0]['cards'][number]) => ReactNode;
  /**
   * 공유 페이지(비회원)의 읽기 전용 갈래 — 계정 얼굴을 **부르는 쪽(서버)이 받아 넘긴다**.
   * 🔑 켜면 이 부품은 로그인 창구로 얼굴을 묻지 않고 상세를 띄우지 않는다.
   * 🔑 영상도 받지 않는다 — 대표 이미지만 깔고(`toPublicStillCard`) 영상 주소를 묻지 않는다(도는 표시·호버 재생·장면 훑기 없음 · 영상 글은 ▶ 배지만).
   */
  publicView?: { profiles: Map<string, AccountProfile> };
  /** 이어 불러오기 — 주면 맨 아래에 닿을 때 `onReachEnd` 를 부른다(쪽을 붙이는 일은 부르는 쪽 몫) */
  more?: { hasMore: boolean; loadingMore: boolean; onReachEnd: () => void };
  /** AI 에게 물을 소재를 고르게 할 때 — 화면이 쥔 장바구니를 그대로 넘긴다(`useHubAiChat`) */
  selection?: {
    selectedKeys: Set<string>;
    selectFull: boolean;
    onToggleSelect: (card: Parameters<typeof MediaGrid>[0]['cards'][number]) => void;
  };
}) {
  // 얼굴은 글 원본의 계정으로 찾는다 — 소재 찾기가 쓰는 길 그대로다
  const posts = useMemo(() => cards.map((card) => card.post).filter((post): post is Record<string, unknown> => Boolean(post)), [cards]);
  const fetched = useSearchAllAccountProfiles(publicView ? NO_POSTS : posts);
  const profiles = publicView?.profiles ?? fetched;
  const faced = useMemo(() => {
    const withFaces = applyAccountProfiles(cards, profiles);
    return publicView ? withFaces.map(toPublicStillCard) : withFaces;
  }, [cards, profiles, publicView]);

  /** 카드 key → 걸린 대목 — 찾을 말이 없으면 빈 지도(그때는 줄 자체를 안 그린다) */
  const snippets = useMemo(() => {
    const out = new Map<string, MatchSnippet>();
    const needle = match?.trim() ?? '';
    if (!needle) return out;
    faced.forEach((card) => {
      const texts = cardMatchTexts(card);
      /**
       * ⚠️ 못 찾는 것이 **정상**이다 — 허브가 찾는 말은 정규 브랜드명(`메디큐브`)인데 글은 별칭으로
       *   적는다(`medicube`). 색인은 정규값으로 걸었으니 글은 맞다. 그때는 글머리를 대신 보여 준다.
       */
      const hit = findMatchSnippet(needle, texts, [card.name, card.partner].filter(Boolean)) ?? leadSnippet(texts);
      if (hit) out.set(card.key, hit);
    });
    return out;
  }, [faced, match]);

  /** 어떤 보기로 펼지 — 화면마다 따로 기억한다(고르면 그 구역만 바뀐다) */
  const [ownView, setOwnView] = useState<MediaView>(MEDIA_DEFAULT_VIEW);
  const view = controlledView ?? ownView;

  /** 테이블이 편 순서(카드 key) — 테이블은 제 정렬·페이지로 카드 순서와 다르게 편다 */
  const [tableOrder, setTableOrder] = useState<string[] | null>(null);
  const handleOrder = useCallback((keys: string[]) => {
    setTableOrder((prev) => (sameList(prev, keys) ? prev : keys));
  }, []);
  /** 상세의 이전/다음이 따를 순서 — 화면에 편 순서 그대로 */
  const shownOrder = useMemo(
    () => (view === 'table' && tableOrder ? orderCardsByKeys(faced, tableOrder) : faced),
    [view, tableOrder, faced],
  );

  const { open, videoUrls, detail } = useListMediaDetail(shownOrder);

  /** 상세에 칠할 말 — 부르는 쪽이 상세 옵션으로 준 것이 이긴다 */
  const shownDetailOptions = useMemo(
    () => (highlight && detailOptions?.highlight === undefined ? { ...detailOptions, highlight } : detailOptions),
    [detailOptions, highlight],
  );

  return (
    <>
      {!controlledView && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <MediaViewSwitch value={view} onChange={setOwnView} />
        </Stack>
      )}

      <MediaGrid
        view={view}
        perRow={perRow}
        maxRowHeight={HUB_ROW_MAX_H}
        cards={faced}
        loading={loading}
        loadingMore={more?.loadingMore ?? false}
        hasMore={more?.hasMore ?? false}
        videos={publicView ? PUBLIC_NO_VIDEOS : videoUrls.results}
        onReachEnd={more?.onReachEnd ?? noop}
        onNeedVideo={publicView ? noop : videoUrls.need}
        onReleaseVideo={publicView ? undefined : videoUrls.release}
        onVideoFail={publicView ? noop : videoUrls.report}
        onOpen={open}
        snippets={snippets}
        showSnippet={Boolean(match?.trim())}
        selectedKeys={selection?.selectedKeys ?? NO_KEYS}
        selectFull={selection?.selectFull ?? false}
        onToggleSelect={selection?.onToggleSelect ?? noop}
        readOnly={readOnly ?? Boolean(publicView)}
        // 발치에 「마지막 소재입니다」는 두지 않는다 — 허브는 일부만 펴는 자리라 «다 봤다»는 거짓말이 된다
        endless={false}
        selectable={Boolean(selection)}
        overlay={overlay}
        cardActions={cardActions}
        singleColumnNarrow={singleColumnNarrow}
        onOrderChange={handleOrder}
        highlight={highlight}
        emphasizedKeys={emphasizedKeys}
      />

      {!publicView && <HubMediaDetail {...detail} socialOnly={socialOnly} options={shownDetailOptions} />}
    </>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 허브는 훑는 자리라 카드를 크게 세운다 — 넓은 화면에서 한 줄에 셋 */
const HUB_PER_ROW = 3;
/** 다만 이보다 높아지지는 않는다 — 아주 넓은 화면에서 셋을 고집하면 한 장이 1,000px 을 넘는다 */
const HUB_ROW_MAX_H = 520;

const NO_KEYS = new Set<string>();
const NO_POSTS: Record<string, unknown>[] = [];
const noop = () => undefined;
